/**
 * Thin server-function wrappers for the 6-Figure Readiness Score. Attempt ids
 * stay server-side: the public browser holds an httpOnly cookie, and in-app
 * calls resolve the signed-in coach's open attempt.
 */

import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { readinessAnswersSchema } from "~/lib/shared/readiness/engine";
import { readinessQuestionEventSchema } from "~/lib/shared/readiness/funnel";
import { READINESS_RERUN_REFUSED_PATH } from "~/lib/shared/readiness/rerun";

const EMPTY_INPUT = z.object({}).optional();
const answersInput = z.object({ answers: readinessAnswersSchema() });
const publicStartInput = z.object({
  firstName: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(254),
});

// Only handler bodies are compiled out of the client build, so this shared
// helper's server imports need the server-only wrapper to stay out of it.
const readPublicCookie = createServerOnlyFn(async () => {
  const { getCookie } = await import("@tanstack/react-start/server");
  const { COOKIE_NAMES } = await import("~/lib/cookies");
  const { parseReadinessPublicCookie } =
    await import("~/lib/server/readiness/assessments");
  return parseReadinessPublicCookie(
    getCookie(COOKIE_NAMES.readinessAssessment),
  );
});

export const startPublicReadinessFn = createServerFn({ method: "POST" })
  .validator(publicStartInput)
  .handler(async ({ data }): Promise<void> => {
    const {
      READINESS_PUBLIC_COOKIE_MAX_AGE_SECONDS,
      serializeReadinessPublicCookie,
      startPublicReadiness,
    } = await import("~/lib/server/readiness/assessments");
    const previous = await readPublicCookie();
    const cookie = await startPublicReadiness({ ...data, cookie: previous });
    if (previous?.accessToken === cookie.accessToken) return;
    const { getRequestUrl, setCookie } =
      await import("@tanstack/react-start/server");
    const { COOKIE_NAMES } = await import("~/lib/cookies");
    setCookie(
      COOKIE_NAMES.readinessAssessment,
      serializeReadinessPublicCookie(cookie),
      {
        path: "/",
        httpOnly: true,
        sameSite: "lax",
        secure:
          process.env.NODE_ENV === "production" ||
          getRequestUrl().protocol === "https:",
        maxAge: READINESS_PUBLIC_COOKIE_MAX_AGE_SECONDS,
      },
    );
  });

export const getPublicReadinessFn = createServerFn({ method: "GET" })
  .validator(EMPTY_INPUT)
  .handler(async () => {
    const cookie = await readPublicCookie();
    if (!cookie) return null;
    const [{ readPublicReadiness }, { auth }, { rethrowClerkAuthError }] =
      await Promise.all([
        import("~/lib/server/readiness/assessments"),
        import("@clerk/tanstack-react-start/server"),
        import("~/lib/auth/clerk-handshake"),
      ]);
    const { userId } = await auth().catch(rethrowClerkAuthError);
    // Signed in, she answers on her account; only a finished anonymous result
    // stays here, for the trial button to claim.
    return readPublicReadiness(cookie, { finishedOnly: userId !== null });
  });

export const completePublicReadinessFn = createServerFn({ method: "POST" })
  .validator(answersInput)
  .handler(async ({ data }) => {
    const cookie = await readPublicCookie();
    const { completePublicReadiness, READINESS_ATTEMPT_EXPIRED_MESSAGE } =
      await import("~/lib/server/readiness/assessments");
    if (!cookie) {
      const { UserFacingError } =
        await import("~/lib/shared/user-facing-error");
      throw new UserFacingError(READINESS_ATTEMPT_EXPIRED_MESSAGE);
    }
    return completePublicReadiness({ cookie, answers: data.answers });
  });

export const recordPublicReadinessQuestionEventFn = createServerFn({
  method: "POST",
})
  .validator(readinessQuestionEventSchema)
  .handler(async ({ data }): Promise<void> => {
    const cookie = await readPublicCookie();
    if (!cookie) return;
    const { recordReadinessQuestionEvent } =
      await import("~/lib/server/readiness/assessments");
    await recordReadinessQuestionEvent({ ...data, access: { cookie } });
  });

export const getMyReadinessFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(EMPTY_INPUT)
  .handler(async ({ context }) => {
    const { readOwnedReadiness } =
      await import("~/lib/server/readiness/assessments");
    const { hasRetiredBusinessHealthScore } =
      await import("~/lib/server/nova/retired-work");
    const [owned, hadRetiredScore] = await Promise.all([
      readOwnedReadiness(context.user.id),
      hasRetiredBusinessHealthScore({ by: "user", id: context.user.id }),
    ]);
    return {
      runs: owned.runs.map(({ completedAt, result }) => ({
        completedAt,
        result,
      })),
      nextRerunAt: owned.nextRerunAt,
      /** A Business Health Score from before; its answers cannot carry over. */
      hadRetiredScore,
    };
  });

/** Her intake's client revenue, which can lower the stage her score gives. */
export const getMyClientRevenueEstimateFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<number | null> => {
    const { readClientRevenueEstimate } =
      await import("~/lib/server/nova/coach-intake-deliverable");
    return readClientRevenueEstimate(context.coach.id);
  });

export const startMyReadinessFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(EMPTY_INPUT)
  .handler(async ({ context }) => {
    const { startOwnedReadiness } =
      await import("~/lib/server/readiness/assessments");
    return startOwnedReadiness({ user: context.user });
  });

export const completeMyReadinessFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(answersInput)
  .handler(async ({ context, data }) => {
    const { completeOwnedReadiness } =
      await import("~/lib/server/readiness/assessments");
    return completeOwnedReadiness({
      user: context.user,
      answers: data.answers,
    });
  });

export const recordMyReadinessQuestionEventFn = createServerFn({
  method: "POST",
})
  .middleware([authMiddleware])
  .validator(readinessQuestionEventSchema)
  .handler(async ({ context, data }): Promise<void> => {
    const { recordReadinessQuestionEvent } =
      await import("~/lib/server/readiness/assessments");
    await recordReadinessQuestionEvent({
      ...data,
      access: { user: context.user },
    });
  });

/**
 * The trial button on the public results page, after she has verified her
 * email. Her coach account is created if needed, the result in this browser
 * moves to it, and she goes to checkout; the trial lands her in NOVA.
 */
export const startReadinessTrialFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(EMPTY_INPUT)
  .handler(async ({ context }): Promise<{ url: string }> => {
    const cookie = await readPublicCookie();
    const { claimPublicReadinessForUser } =
      await import("~/lib/server/readiness/assessments");
    const { ensureCoachForUser } =
      await import("~/lib/server/coach-registration");
    const { coachId, needsPaidPlan } = cookie
      ? await claimPublicReadinessForUser({ cookie, user: context.user })
      : {
          coachId: (await ensureCoachForUser(context.user)).id,
          needsPaidPlan: false,
        };

    const { getCoach } = await import("~/lib/server/coach");
    const coach = await getCoach(coachId);
    if (!coach) throw new Error("The coach account was not found");
    const { getRequestUrl } = await import("@tanstack/react-start/server");
    const { createNovaCheckoutUrl } = await import("~/lib/server/nova-billing");
    // A result the rerun rule kept out of her account does not stop the trial
    // she asked for. Checkout then returns to her score page, which says why.
    const url = await createNovaCheckoutUrl({
      coach,
      appUrl: getRequestUrl().origin,
      ...(needsPaidPlan ? { returnTo: READINESS_RERUN_REFUSED_PATH } : {}),
    });
    return { url };
  });
